import { Mail, CreditCard, Clock, Check, XCircle, RotateCcw } from "lucide-react";

const mono = { fontFamily: "'IBM Plex Sans', monospace" };

function PaymentIndicator({ status }) {
  if (!status) return null;
  const map = {
    invoice_sent: { icon: Clock, color: "text-amber-400", tip: "Invoice sent — payment pending" },
    paid: { icon: Check, color: "text-emerald-400", tip: "Paid — customer payment received" },
    voided: { icon: XCircle, color: "text-muted-foreground/60", tip: "Voided — invoice canceled in Square" },
    refunded: { icon: RotateCcw, color: "text-red-400", tip: "Refunded — refund recorded from Square" },
  };
  const m = map[status];
  if (!m) return null;
  const Icon = m.icon;
  return <Icon className={`w-3 h-3 mx-auto ${m.color}`} title={m.tip} />;
}

function formatMilFull(h, m) {
  return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
}

function formatChildSeats(job) {
  const rear = job.child_seats_rear || 0;
  const front = job.child_seats_front || 0;
  const booster = job.child_seats_booster || 0;
  if (rear === 0 && front === 0 && booster === 0) return null;
  const parts = [];
  if (front === 1) parts.push("FF");
  else if (front > 1) parts.push(`${front} FF`);
  if (rear === 1) parts.push("RF");
  else if (rear > 1) parts.push(`${rear} RF`);
  if (booster === 1) parts.push("BS");
  else if (booster > 1) parts.push(`${booster} BS`);
  return parts.join("+");
}

function formatPhone(raw) {
  if (!raw) return "—";
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    const d = digits.slice(1);
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }
  return raw;
}

const AIRPORT_MAP = [
  { patterns: ["john f. kennedy", "jfk", "kennedy airport", "kennedy international"], code: "JFK" },
  { patterns: ["laguardia", "lga"], code: "LGA" },
  { patterns: ["newark liberty", "newark airport", "ewr"], code: "EWR" },
  { patterns: ["teterboro", "teb"], code: "TEB" },
  { patterns: ["westchester county airport", "hpn", "white plains airport"], code: "HPN" },
];

function abbreviateAirport(location) {
  if (!location) return "—";
  const lower = location.toLowerCase();
  for (const apt of AIRPORT_MAP) {
    for (const pat of apt.patterns) {
      if (lower.includes(pat)) {
        // Extract terminal info
        const termMatch = lower.match(/terminal\s*([a-z0-9]+)/i) || lower.match(/\bt([1-9])\b/i);
        const term = termMatch ? ` T${termMatch[1].toUpperCase()}` : "";
        return `${apt.code}${term}`;
      }
    }
  }
  return location;
}

/**
 * Shared column definitions for job tables (Day View, Upcoming View).
 *
 * Width strategy: known-max columns get min-w only (browser auto-fits to content).
 * Variable columns get no width — table-layout:auto distributes remaining space.
 * Pickup/Dropoff truncate with overflow to prevent blowout.
 */
export const JOB_COLUMNS = [
  { key: "job_number", label: "Job#", w: "min-w-[44px]", render: (job) => (
    <span className="text-[10px] font-semibold text-muted-foreground whitespace-nowrap" style={mono}>
      {job.job_number ? `#${job.job_number}` : "—"}
    </span>
  )},
  { key: "time", label: "Time", w: "min-w-[48px]", render: (job) => (
    <span className="font-semibold text-xs whitespace-nowrap" style={mono}>
      {job.pickup_datetime ? formatMilFull(new Date(job.pickup_datetime).getHours(), new Date(job.pickup_datetime).getMinutes()) : "—"}
    </span>
  )},
  { key: "client", label: "Client", render: (job) => (
    <span className="text-xs font-medium truncate block" title={job.client_name || `${job.first_name || ""} ${job.last_name || ""}`.trim() || ""}>{job.client_name || `${job.first_name || ""} ${job.last_name || ""}`.trim() || "—"}</span>
  )},
  { key: "phone", label: "Phone", render: (job) => (
    <span className="text-[10px] whitespace-nowrap font-semibold" style={mono}>{formatPhone(job.client_phone)}</span>
  )},
  { key: "pickup", label: "Pickup", w: "max-w-[180px]", render: (job) => (
    <span className="text-xs truncate block" title={job.pickup_location}>{abbreviateAirport(job.pickup_location)}</span>
  )},
  { key: "dropoff", label: "Dropoff", w: "max-w-[180px]", render: (job) => (
    <span className="text-xs truncate block" title={job.dropoff_location}>{abbreviateAirport(job.dropoff_location)}</span>
  )},
  { key: "pax", label: "Pax", w: "min-w-[28px]", render: (job) => (
    <span className="text-xs text-center block">{job.passenger_count}</span>
  )},
  { key: "luggage", label: "Lug", render: (job) => (
    <span className="text-xs whitespace-nowrap">{job.luggage || "—"}</span>
  )},
  { key: "class", label: "Class", render: (job) => (
    <span className="text-xs whitespace-nowrap">{(job.vehicle_class || job.vehicle_type || "").toUpperCase() === "SUV" ? "SUV" : (job.vehicle_class || job.vehicle_type || "—").charAt(0).toUpperCase() + (job.vehicle_class || job.vehicle_type || "—").slice(1)}</span>
  )},
  { key: "driver", label: "Dr#", w: "min-w-[42px]", isDriverCell: true, cellClass: (job) => {
    const has = !!job.driver_id || !!job.driver_number;
    return has ? "" : "bg-amber-500/20";
  }, render: (job) => (
    <span className="text-xs text-center block font-semibold text-cyan-700 dark:text-cyan-300 whitespace-nowrap" style={mono}>
      {job.driver_number || ""}
    </span>
  )},
  { key: "seats", label: "Seats", render: (job) => {
    const s = formatChildSeats(job);
    return <span className="text-[10px] text-center block whitespace-nowrap" style={mono}>
      {s ? <span className="text-orange-300">{s}</span> : <span className="text-muted-foreground/40">X</span>}
    </span>;
  }},
  { key: "mg", label: "M&G", w: "min-w-[32px]", render: (job) => {
    const has = (job.special_services || []).includes("meet_greet");
    return <span className="text-xs text-center block font-semibold">
      {has ? <span className="text-primary">+</span> : <span className="text-muted-foreground/40">X</span>}
    </span>;
  }},
  { key: "flag", label: "S", w: "min-w-[24px]", render: (job) => {
    if (job.status === "no_show") {
      return <span className="text-[9px] font-bold text-red-500 tracking-wider" title="No-show">NS</span>;
    }
    if (job.status === "completed") {
      return <span className="inline-block w-2 h-2 rounded-sm bg-emerald-400 rotate-45" title="Completed" />;
    }
    const note = (job.special_instructions || "").trim();
    return note ? <span className="inline-block w-2 h-2 rounded-sm bg-orange-400 rotate-45" title={note} /> : null;
  }},
  { key: "email", label: "", w: "min-w-[24px]", isEmailCell: true, render: (job) => {
    const hasDriver = !!job.driver_id;
    return (
      <Mail className={`w-3 h-3 mx-auto ${hasDriver ? "text-primary cursor-pointer" : "text-muted-foreground/20"}`} />
    );
  }},
  { key: "payment", label: "", w: "min-w-[24px]", isPaymentCell: true, render: (job) => (
    <PaymentIndicator status={job.payment_status} />
  )},
];

/** Table cell density classes — apply to all job tables for uniformity */
export const CELL_CLASS = "px-1.5 py-1";
export const HEADER_CLASS = "px-1.5 py-1.5 text-[11px]";
